import { Typography } from '@mgmresorts/wcl-lab';
import React, { useCallback, useContext } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import accountSignUpIcon from '../../components/ui/icons/account-activated.svg';
import { useContent } from '../../hooks/useContent';

import { Address } from './Address';
import { BirthdayExtendedInput } from './BirthdayExtendedInput';
import { BirthdayInput } from './BirthdayInput';
import { CTASection } from './CTASection';
import { CanadianOptIn } from './CanadianOptIn';
import { EmailInput } from './EmailInput';
import { FirstName } from './FirstName';
import { LastName } from './LastName';
import { PasswordInput } from './PasswordInput';
import { PhoneNumberInput } from './PhoneNumberInput';
import { PolicyCheckbox } from './PolicyCheckbox';
import { SignUpFormContext } from './SignUpFormContext';
import { SignUpVerification } from './SignUpVerification';
import StyledContent from './StyledContent';

export function SignUpForm() {
  const { enrollmentError, showExtendedBirthday, isLoading, isCanadian, showWelcomeMgmRewardsScreen, verificationKid } =
    useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const isEnrollmentAnnouncementEnabled = process.env.REACT_APP_ENABLE_ENROLLMENT_ANNOUNCEMENT === 'true';

  const renderFormContent = useCallback(() => {
    return (
      <Container height='auto'>
        <Logo className='identity__page-logo' />
        <Typography variant='h1' className='identity__page-heading' testId='page-title'>
          {getContentByPath('accountCreation.title')}
        </Typography>
        {isEnrollmentAnnouncementEnabled && (
          <Alert text={getContentByPath('accountCreation.announcement')} type='info' />
        )}
        {!isLoading && enrollmentError && <Alert text={enrollmentError} />}
        <Typography variant='body' className='identity__page-info'>
          {getContentByPath('accountCreation.info')}
        </Typography>
        <EmailInput />
        <PasswordInput />
        <div className='identity__page-guest-info'>
          <Typography variant='h2' className='identity__page-sub-heading' testId='page-sub-title'>
            {getContentByPath('accountCreation.subTitle.guest')}
          </Typography>
          <FirstName />
          <LastName />
          {showExtendedBirthday ? <BirthdayExtendedInput /> : <BirthdayInput />}
          <PhoneNumberInput />
        </div>
        <Address />
        {isCanadian && <CanadianOptIn />}
        <PolicyCheckbox />
        <CTASection />
      </Container>
    );
  }, [getContentByPath, isCanadian, enrollmentError, showExtendedBirthday, isLoading, isEnrollmentAnnouncementEnabled]);

  const renderSuccessContent = useCallback(() => {
    return (
      <Container height='auto'>
        <div className='identity__welcome-page'>
          <img src={accountSignUpIcon} alt={getContentByPath('accountCreation.welcomeMgmPageInfo')} />
          <Typography variant='paragraph' element='p' className='identity__welcome-page-info'>
            {getContentByPath('accountCreation.welcomeMgmPageInfo')}
          </Typography>
        </div>
      </Container>
    );
  }, [getContentByPath]);

  return (
    <StyledContent>
      <PageLayout
        testId='route-/signup'
        title={getContentByPath('accountCreation.metaTitle')}
        description={getContentByPath('accountCreation.description')}
      >
        {showWelcomeMgmRewardsScreen ? (
          renderSuccessContent()
        ) : verificationKid ? (
          <SignUpVerification />
        ) : (
          renderFormContent()
        )}
      </PageLayout>
    </StyledContent>
  );
}

SignUpForm.displayName = 'SignUpForm';
